import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Card } from '@/components/ui/card'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { Progress } from '@/components/ui/progress'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { getI18nApiTypeName } from '@/features/account/components/sheets/account/apiSection/i18n'
import { cn } from '@/lib/utils'
import { accountCategoryList } from '@pkg/basic'
import { ChevronDownIcon } from '@radix-ui/react-icons'
import type { TFunction } from 'i18next'
import { useAtom } from 'jotai'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  exchangeApiProgressesAtom,
  progressesAtom,
} from '../../states/progress'
import type { AnalyzeProgress, ExchangeApiProgress } from '../../types'

const AnalyzeProgressToast = () => {
  const { t } = useTranslation()
  const [progresses] = useAtom(progressesAtom)
  const [exchangeApiProgressRecord] = useAtom(exchangeApiProgressesAtom)
  const [open, setOpen] = useState(false)

  const exchangeApiProgress = Object.values(exchangeApiProgressRecord)
    .filter(
      (progress): progress is ExchangeApiProgress => progress !== undefined,
    )
    .sort((a, b) => a.category.localeCompare(b.category))

  useEffect(() => {
    if (progresses.length === 0 && exchangeApiProgress.length === 0) {
      setOpen(false)
    }
  }, [progresses, exchangeApiProgress])

  return (
    <div
      className={cn(
        'fixed bottom-0 left-0 z-50 p-2 duration-500',
        progresses.length === 0 &&
          exchangeApiProgress.length === 0 &&
          'translate-y-[64px]',
      )}
    >
      <Card className="w-[360px] p-2">
        <Collapsible open={open} onOpenChange={setOpen}>
          <CollapsibleTrigger className="w-full">
            <div className="flex w-full items-center justify-between px-2">
              <p className="font-bold text-muted-foreground text-sm">
                {t('Syncing {{size}} chains / exchanges', {
                  size: exchangeApiProgress.length + progresses.length,
                })}
              </p>
              <div className="ml-4">
                <ChevronDownIcon className={open ? 'rotate-180' : ''} />
              </div>
            </div>
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-2 max-h-[112px] divide-y overflow-y-scroll border-t">
            {exchangeApiProgress.map((progress) => (
              <ExchangeApiProgressItem
                key={progress.category}
                progress={progress}
              />
            ))}
            {progresses.map((progress) => (
              <ProgressItem key={progress.category} progress={progress} />
            ))}
          </CollapsibleContent>
        </Collapsible>
      </Card>
    </div>
  )
}

const getProgressStatusText = (
  progress: AnalyzeProgress,
  t: TFunction,
): string => {
  switch (progress.status) {
    case 'analyze': {
      return t('Analyzing')
    }
    case 'calculate': {
      return t('Calculating Profit & Loss')
    }
    case 'check-account': {
      return t('Checking Account')
    }
    case 'sync': {
      return t('Syncing')
    }
    default: {
      throw new Error(`Unknown value: ${progress.status satisfies never}`)
    }
  }
}

const ProgressItem = ({ progress }: { progress: AnalyzeProgress }) => {
  const { t } = useTranslation()

  const category = accountCategoryList.find(
    ({ id }) => id === progress.category,
  )
  if (category === undefined) {
    return <></>
  }

  return (
    <div className="flex w-full items-center space-x-2 px-3 py-2">
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Avatar className="h-4 w-4">
              <AvatarImage src={category.icon} />
              <AvatarFallback className="bg-primary">
                {category.name[0].toUpperCase()}
              </AvatarFallback>
            </Avatar>
          </TooltipTrigger>
          <TooltipContent className="bg-secondary text-secondary-foreground">
            <p>{category.name}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <p className="w-2/5 text-sm">{getProgressStatusText(progress, t)}</p>
      <div className="flex-1 pl-2">
        <Progress value={progress.progress} />
      </div>
      <p className="text-sm">{Math.floor(progress.progress)} %</p>
    </div>
  )
}

const ExchangeApiProgressItem = ({
  progress,
}: { progress: ExchangeApiProgress }) => {
  const { t } = useTranslation()

  const percent = useMemo(
    () => (progress.range > 0 ? (progress.synced / progress.range) * 100 : 0),
    [progress.range, progress.synced],
  )

  const typeName = getI18nApiTypeName({
    category: progress.category,
    type: progress.type,
    t,
  })

  const category = accountCategoryList.find(
    ({ id }) => id === progress.category,
  )
  if (category === undefined) {
    return <></>
  }

  return (
    <div className="flex w-full items-center space-x-2 px-3 py-2">
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Avatar className="h-4 w-4">
              <AvatarImage src={category.icon} />
              <AvatarFallback className="bg-primary">
                {category.name[0].toUpperCase()}
              </AvatarFallback>
            </Avatar>
          </TooltipTrigger>
          <TooltipContent className="bg-secondary text-secondary-foreground">
            <p>{category.name}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <p className="w-2/5 text-sm">
        {Math.min(progress.done + 1, progress.size)} / {progress.size} |{' '}
        {typeName}
        {progress.subType ? ` (${progress.subType})` : ''}
      </p>
      <div className="flex-1 pl-2">
        <Progress value={percent} />
      </div>
      <p className="text-sm">{Math.floor(percent)} %</p>
    </div>
  )
}

export default AnalyzeProgressToast
